
"use client";

import { useEffect, useState, useMemo } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Copy, Loader2, Share2, Users, DollarSign } from 'lucide-react';
import type { WalletTransaction, User as AppUser, LoyaltySettings } from "@/lib/types";

export default function ReferralsPage() {
    const [user, setUser] = useState<User | null>(null);
    const [appUser, setAppUser] = useState<AppUser | null>(null);
    const [loyaltySettings, setLoyaltySettings] = useState<LoyaltySettings | null>(null);
    const [referralTransactions, setReferralTransactions] = useState<WalletTransaction[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (!currentUser) {
                setIsLoading(false);
            }
        });
        
        getDoc(doc(db, 'settings', 'loyalty')).then(doc => {
            if(doc.exists()) setLoyaltySettings(doc.data() as LoyaltySettings);
        });

        return () => unsubscribeAuth();
    }, []);

    useEffect(() => {
        if (!user) return;
        
        let unsubUser: () => void;
        let unsubTransactions: () => void;

        const fetchData = () => {
            const userDocRef = doc(db, 'users', user.uid);
            unsubUser = onSnapshot(userDocRef, (doc) => {
                if (doc.exists()) {
                    setAppUser(doc.data() as AppUser);
                }
            });

            const transactionsQuery = query(
                collection(db, "walletTransactions"), 
                where("userId", "==", user.uid),
                where("type", "==", "Bônus Indicação")
            );

            unsubTransactions = onSnapshot(transactionsQuery, (snapshot) => {
                setReferralTransactions(snapshot.docs.map(doc => doc.data() as WalletTransaction));
                setIsLoading(false);
            }, (error) => {
                console.error("Error fetching referral transactions:", error);
                toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível buscar seu histórico de indicações.' });
                setIsLoading(false);
            });
        };

        fetchData();

        return () => {
            if (unsubUser) unsubUser();
            if (unsubTransactions) unsubTransactions();
        };

    }, [user]);

    const referralStats = useMemo(() => {
        const totalEarnings = referralTransactions.reduce((acc, t) => acc + t.amount, 0);
        const totalReferrals = referralTransactions.length;
        return { totalEarnings, totalReferrals };
    }, [referralTransactions]);

    const referralLink = appUser?.referralCode ? `${window.location.origin}/r/${appUser.referralCode}` : '';

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: "Copiado!", description: "O link de indicação foi copiado." });
    };

    if (isLoading) {
        return <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin h-8 w-8" /></div>;
    }
    
    if (!user || !appUser) {
        return <div className="text-center"><p>Você precisa estar logado para ver esta página.</p></div>
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold font-headline">Programa de Indicação</h1>
            
            <Card>
                <CardHeader>
                    <CardTitle>Seu Código de Indicação</CardTitle>
                    <CardDescription>
                        Compartilhe seu código com amigos. Quando eles fizerem a primeira compra usando seu código, você ganha <strong>{loyaltySettings?.referralBonus || 5}%</strong> do valor da compra deles como cashback na sua carteira!
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <Label htmlFor="referral-code">Seu Código Pessoal</Label>
                        <div className="flex items-center space-x-2">
                            <Input id="referral-code" readOnly value={appUser.referralCode || '...'} className="font-mono text-lg tracking-wider" />
                            <Button variant="outline" size="icon" onClick={() => handleCopy(appUser.referralCode || '')}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                     <div>
                        <Label htmlFor="referral-link">Seu Link de Indicação</Label>
                        <div className="flex items-center space-x-2">
                            <Input id="referral-link" readOnly value={referralLink} className="text-sm" />
                            <Button variant="outline" size="icon" onClick={() => handleCopy(referralLink)}>
                                <Share2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total de Indicados</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{referralStats.totalReferrals}</div>
                        <p className="text-xs text-muted-foreground">
                            Pessoas que compraram com seu código.
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Ganhos Totais com Indicação</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">R$ {referralStats.totalEarnings.toFixed(2)}</div>
                        <p className="text-xs text-muted-foreground">
                            Soma de todo o cashback recebido.
                        </p>
                    </CardContent>
                </Card>
            </div>
            
             <Card>
                <CardHeader>
                    <CardTitle>Histórico de Ganhos</CardTitle>
                    <CardDescription>
                        Acompanhe o cashback recebido de cada indicação.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {referralTransactions.length > 0 ? referralTransactions.map(tx => (
                            <div key={tx.id} className="flex justify-between items-center p-3 bg-muted rounded-md text-sm">
                                <div>
                                    <p className="font-semibold">{tx.description}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {new Date(tx.createdAt.seconds * 1000).toLocaleDateString('pt-BR')}
                                    </p>
                                </div>
                                <p className="font-bold text-green-400">+ R$ {tx.amount.toFixed(2)}</p>
                            </div>
                        )) : (
                            <p className="text-center text-muted-foreground py-4">Nenhum ganho de indicação ainda.</p>
                        )}
                    </div>
                </CardContent>
             </Card>
        </div>
    );
}
